import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import Header from './header';
import Sidebar from './sidebar';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isSuperAdmin, getSuperAdminHomePath } from '../Utils/superAdminAccess';
import { syncHeadOfLineRoleFromApi } from '../Utils/headOfLineRole';
import {
  isAdminWithoutActiveSubscription,
  isUserMemberWithoutLineSubscription,
  isSubscriptionSettingsLocation,
  ADMIN_SUBSCRIPTION_MODAL_DISMISSED_KEY,
  OPEN_ADMIN_SUBSCRIPTION_MODAL_EVENT,
  OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT,
  SETTINGS_SUBSCRIPTION_PATH,
} from '../Utils/subscriptionAccess';
import AdminSubscriptionRequiredModal from '../Modals/AdminSubscriptionRequiredModal';
import UserLineSubscriptionModal from '../Modals/UserLineSubscriptionModal';
import { FamilyTreeMobileNoticeHost, isMobileViewport } from '../Components/Shared/FeatureNoticeModal';
import useDashboardKeyboardReset from '../Components/AdminComponents/DashboardComponents/useDashboardKeyboardReset';

const Layout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [roleSynced, setRoleSynced] = useState(false);
  const [roleTick, setRoleTick] = useState(0);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showUserLineSubscriptionModal, setShowUserLineSubscriptionModal] = useState(false);

  // Phones: the dashboard reports' header and legend come back on screen
  // after the keyboard closes.
  useDashboardKeyboardReset();

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  /**
   * Mobile only: the expanded drawer sits on top of the page, so a tap on a nav
   * row changed the route behind a sidebar that stayed open and the tab looked
   * like it never got selected. Collapse back to the icon rail after navigating
   * so the newly selected tab and its page are both visible. Desktop keeps the
   * sidebar exactly as the user left it.
   */
  const handleSidebarNavigate = useCallback(() => {
    if (isMobileViewport()) setIsCollapsed(true);
  }, []);

  useEffect(() => {
    if (isSuperAdmin()) {
      navigate(getSuperAdminHomePath(), { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await syncHeadOfLineRoleFromApi();
      if (!cancelled) setRoleSynced(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const bump = () => setRoleTick((t) => t + 1);
    window.addEventListener('loggedInUserRoleUpdated', bump);
    return () => window.removeEventListener('loggedInUserRoleUpdated', bump);
  }, []);

  useEffect(() => {
    const openModal = () => {
      if (isSubscriptionSettingsLocation(location)) return;
      if (isAdminWithoutActiveSubscription()) {
        setShowSubscriptionModal(true);
      }
    };
    window.addEventListener(OPEN_ADMIN_SUBSCRIPTION_MODAL_EVENT, openModal);
    return () => window.removeEventListener(OPEN_ADMIN_SUBSCRIPTION_MODAL_EVENT, openModal);
  }, [location.pathname, location.search]);

  useEffect(() => {
    /** Sidebar/header reopen line-fee modal unless user is already on Subscription Management. */
    const openUserLineModal = () => {
      if (isSubscriptionSettingsLocation(location)) return;
      if (!isUserMemberWithoutLineSubscription()) return;
      setShowUserLineSubscriptionModal(true);
    };
    window.addEventListener(OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT, openUserLineModal);
    return () => window.removeEventListener(OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT, openUserLineModal);
  }, [location.pathname, location.search]);

  const needsSubscription = isAdminWithoutActiveSubscription();

  useLayoutEffect(() => {
    if (!roleSynced) return;
    if (!needsSubscription) return;
    if (location.pathname.startsWith('/settings')) return;
    navigate(SETTINGS_SUBSCRIPTION_PATH, { replace: true });
  }, [roleSynced, needsSubscription, location.pathname, navigate]);

  useEffect(() => {
    if (!needsSubscription) {
      setShowSubscriptionModal(false);
      return;
    }
    if (isSubscriptionSettingsLocation(location)) {
      setShowSubscriptionModal(false);
      return;
    }
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(ADMIN_SUBSCRIPTION_MODAL_DISMISSED_KEY) === '1';
    } catch (_) {
      /* ignore */
    }
    setShowSubscriptionModal(!dismissed);
  }, [needsSubscription, location.pathname, location.search, roleTick]);

  useEffect(() => {
    if (!isUserMemberWithoutLineSubscription()) {
      setShowUserLineSubscriptionModal(false);
      return;
    }
    if (isSubscriptionSettingsLocation(location)) {
      setShowUserLineSubscriptionModal(false);
    }
  }, [roleTick, location.pathname, location.search]);

  // Add/remove class on body for CSS selector support
  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [isCollapsed]);

  // Mobile: sidebar starts expanded like desktop; user can collapse via the collapse toggle
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => {
      if (mq.matches) {
        document.body.classList.add('app-layout-mobile');
      } else {
        document.body.classList.remove('app-layout-mobile');
      }
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.body.classList.remove('app-layout-mobile');
    };
  }, []);

  return (
    <div className={`app-layout flex h-screen bg-gray-50${isCollapsed ? '' : ' app-layout--sidebar-open'}`}>
      <AdminSubscriptionRequiredModal
        isOpen={showSubscriptionModal && needsSubscription}
        onClose={() => setShowSubscriptionModal(false)}
      />
      <UserLineSubscriptionModal
        isOpen={showUserLineSubscriptionModal && isUserMemberWithoutLineSubscription()}
        onClose={() => setShowUserLineSubscriptionModal(false)}
      />
      <FamilyTreeMobileNoticeHost />

      {/* Sidebar */}
      <div className={`app-layout-sidebar${isCollapsed ? ' app-layout-sidebar--collapsed' : ''}`}>
        <Sidebar
          isCollapsed={isCollapsed}
          onToggle={toggleSidebar}
          onNavigate={handleSidebarNavigate}
        />
      </div>

      {/* Mobile overlay: expanded sidebar sits above content without shifting it */}
      {!isCollapsed && (
        <button
          type="button"
          className="app-layout-sidebar-backdrop"
          aria-label="Close sidebar"
          onClick={() => setIsCollapsed(true)}
        />
      )}

      {/* Main Content Area */}
      <div
        className={`app-layout-main flex-1 flex flex-col transition-all duration-300 ease-in-out top-header-fix${
          isCollapsed ? ' app-layout-main--collapsed' : ''
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 z-30">
          <Header />
        </div>

        {/* Page Content */}
        <main className="app-layout-content flex-1 p-6 bg-gray-50 ">
          {!roleSynced ? (
            <div className="flex flex-1 items-center justify-center p-8 text-gray-500" aria-live="polite">
              Loading…
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
};

export default Layout;
